import { CssBaseline, GlobalStyles } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
    deDE,
    enUS,
    esES,
    fiFI,
    frFR,
    isIS,
    itIT,
    jaJP,
    nbNO,
    plPL,
    ptBR,
    ptPT,
    zhCN,
    zhTW,
} from "@mui/x-date-pickers/locales";
import deLocale from "date-fns/locale/de";
import enAULocale from "date-fns/locale/en-AU";
import enCALocale from "date-fns/locale/en-CA";
import enLocale from "date-fns/locale/en-GB";
import esLocale from "date-fns/locale/es";
import fiLocale from "date-fns/locale/fi";
import frLocale from "date-fns/locale/fr";
import frCALocale from "date-fns/locale/fr-CA";
import isLocale from "date-fns/locale/is";
import itLocale from "date-fns/locale/it";
import jaLocale from "date-fns/locale/ja";
import nbLocale from "date-fns/locale/nb";
import plLocale from "date-fns/locale/pl";
import ptLocale from "date-fns/locale/pt";
import ptBRLocale from "date-fns/locale/pt-BR";
import thLocale from "date-fns/locale/th";
import zhCNLocale from "date-fns/locale/zh-CN";
import zhTWLocale from "date-fns/locale/zh-TW";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { selectIsProcoreMobileDesign, selectUxFlavor } from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";

import { PROCORE_FONT_FAMILY, procoreFontTheme, procoreTheme, theme } from "../app/theme";

// Keyed by the codes registered in src/i18n.ts, lower-cased to match
// `lowerCaseLng`. @mui/x-date-pickers ships no Thai, so th-TH takes English
// picker chrome with Thai date formatting; date-fns has no es-ES or pt-PT, so
// those use the base-language locale.
const localeMap: Record<string, { mui: typeof enUS; dateFns: Locale }> = {
    en: { mui: enUS, dateFns: enLocale },
    "en-au": { mui: enUS, dateFns: enAULocale },
    "en-ca": { mui: enUS, dateFns: enCALocale },
    "en-gb": { mui: enUS, dateFns: enLocale },
    "de-de": { mui: deDE, dateFns: deLocale },
    es: { mui: esES, dateFns: esLocale },
    "es-es": { mui: esES, dateFns: esLocale },
    "fi-fi": { mui: fiFI, dateFns: fiLocale },
    "fr-ca": { mui: frFR, dateFns: frCALocale },
    "fr-fr": { mui: frFR, dateFns: frLocale },
    "is-is": { mui: isIS, dateFns: isLocale },
    "it-it": { mui: itIT, dateFns: itLocale },
    "ja-jp": { mui: jaJP, dateFns: jaLocale },
    "nb-no": { mui: nbNO, dateFns: nbLocale },
    "pl-pl": { mui: plPL, dateFns: plLocale },
    "pt-br": { mui: ptBR, dateFns: ptBRLocale },
    "pt-pt": { mui: ptPT, dateFns: ptLocale },
    "th-th": { mui: enUS, dateFns: thLocale },
    "zh-sg": { mui: zhCN, dateFns: zhCNLocale },
    "zh-tw": { mui: zhTW, dateFns: zhTWLocale },
};

const resolveDateLocale = (language: string | undefined) => {
    const code = (language ?? "").toLowerCase();
    if (localeMap[code]) {
        return localeMap[code];
    }
    // Bare codes such as `de` still reach here via the deprecation aliases in
    // src/i18n.ts, and have no entry of their own. Use any regional variant of
    // the same language rather than dropping to English date formats.
    const base = code.split("-")[0];
    const variant = Object.keys(localeMap).find((key) => key === base || key.startsWith(`${base}-`));
    return variant ? localeMap[variant] : localeMap.en;
};

export function GeneralProvider({ children, uxFlavor: forceUxFlavor }: { children: ReactNode; uxFlavor?: UxFlavor }) {
    const { i18n } = useTranslation();
    // resolvedLanguage, not language: for a deprecated alias the latter stays
    // as the alias (`de`) while the former is what actually loaded (`de-de`).
    const [locale, setLocale] = useState(resolveDateLocale(i18n.resolvedLanguage ?? i18n.language));
    const storeUxFlavor = useAppSelector(selectUxFlavor);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);

    // forceUxFlavor is needed because storeUxFlavor might be not initialized with a proper value
    // at the moment theme is already needed (e.g. app loading screen)
    // So for lib mode we just force it to Procore since the very beginning
    const uxFlavor = forceUxFlavor || storeUxFlavor;

    // Desktop Procore gets the full procoreTheme (palette, components, Inter).
    // Mobile Procore flavors get Inter typography only — palette and components are
    // specifically designed for those platforms and should not be overridden.
    const isProcoreDesktop = uxFlavor === UxFlavor.Procore;
    const isProcoreVariant =
        uxFlavor === UxFlavor.Procore || uxFlavor === UxFlavor.ProcoreAndroid || uxFlavor === UxFlavor.ProcoreIOS;

    useEffect(() => {
        setLocale(resolveDateLocale(i18n.resolvedLanguage ?? i18n.language));
    }, [i18n.language, i18n.resolvedLanguage]);
    return (
        <>
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={locale.dateFns}
                localeText={locale.mui.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <StyledEngineProvider injectFirst>
                    <ThemeProvider
                        theme={isProcoreDesktop ? procoreTheme : isProcoreVariant ? procoreFontTheme : theme}
                    >
                        <CssBaseline />
                        {isProcoreVariant && (
                            <GlobalStyles
                                styles={{
                                    // Scoped to the viewer root so these styles do not leak into
                                    // the host page (Procore chrome, PDFtron toolbar, etc.).
                                    // A class selector (0,1,0) beats PDFtron's runtime-injected
                                    // `body { font-family: "Open Sans" }` (0,0,1), so we no longer
                                    // need the `html body` specificity hack.
                                    ".nw-viewer": {
                                        fontFamily: PROCORE_FONT_FAMILY,
                                    },
                                    // Browser UA stylesheets set font-family explicitly on form
                                    // elements, breaking inheritance. Reset to inherit so they
                                    // pick up Inter from the nearest ancestor.
                                    ".nw-viewer button, .nw-viewer input, .nw-viewer select, .nw-viewer textarea": {
                                        fontFamily: "inherit",
                                    },
                                }}
                            />
                        )}
                        {isMobileDesign && (
                            <GlobalStyles
                                styles={{
                                    // Disable text selection across the entire mobile page, but keep
                                    // form fields selectable so text editing still works.
                                    "html, body": {
                                        userSelect: "none",
                                        WebkitUserSelect: "none",
                                        WebkitTouchCallout: "none",
                                    },
                                    "input, textarea, [contenteditable=true]": {
                                        userSelect: "text",
                                        WebkitUserSelect: "text",
                                    },
                                }}
                            />
                        )}
                        {isProcoreVariant ? (
                            <div className="nw-viewer" style={{ display: "contents" }}>
                                {children}
                            </div>
                        ) : (
                            children
                        )}
                    </ThemeProvider>
                </StyledEngineProvider>
            </LocalizationProvider>
        </>
    );
}
